import React, { useState } from 'react';
import {
    Box,
    Typography,
    Container,
    Grid,
    Card,
    CardContent,
    Button,
    Chip,
    Paper,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Avatar,
    Divider,
    Stack,
    LinearProgress
} from '@mui/material';
import { motion } from 'framer-motion';
import {
    TrendingUp as TrendingUpIcon,
    ShowChart as ShowChartIcon,
    Speed as SpeedIcon,
    Assessment as AssessmentIcon,
    Insights as InsightsIcon,
    AttachMoney as AttachMoneyIcon,
    Schedule as ScheduleIcon,
    FilterList as FilterListIcon,
    GetApp as GetAppIcon,
    CheckCircle as CheckCircleIcon,
    Store as StoreIcon,
    Campaign as CampaignIcon,
    BusinessCenter as BusinessCenterIcon,
    Analytics as AnalyticsIcon,
    AutoAwesome as AutoAwesomeIcon,
    Notifications as NotificationsIcon,
    Timeline as TimelineIcon,
    CompareArrows as CompareArrowsIcon,
    ArrowForward as ArrowForwardIcon
} from '@mui/icons-material';
import {
    LineChart,
    Line,
    AreaChart,
    Area,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer
} from 'recharts';

const TrendCAT = () => {
    // Demo trend data
    const trendData = [
        { date: 'Hf 1', urun_a: 2400, urun_b: 1800, urun_c: 2200 },
        { date: 'Hf 2', urun_a: 2800, urun_b: 2100, urun_c: 2400 },
        { date: 'Hf 3', urun_a: 3200, urun_b: 2500, urun_c: 2800 },
        { date: 'Hf 4', urun_a: 3800, urun_b: 3000, urun_c: 3200 },
        { date: 'Hf 5', urun_a: 4200, urun_b: 3500, urun_c: 3600 }
    ];

    // Ana özellikler
    const mainFeatures = [
        {
            icon: <TrendingUpIcon sx={{ fontSize: 40 }} />,
            title: 'Otomatik Veri Toplama',
            description: 'Web verilerinden popüler ürünleri ve yükselen kategorileri otomatik olarak tespit eder. Manuel analiz yapmadan önemli fırsatları ve riskleri fark etmenizi sağlar.',
            color: '#1e3c72'
        },
        {
            icon: <AssessmentIcon sx={{ fontSize: 40 }} />,
            title: 'Ürün & Kategori Analizi',
            description: 'Ürün ve kategori bazlı detaylı trend raporları. Fiyat eğilimleri ve karşılaştırmalar ile pazar dinamiklerini anında görün.',
            color: '#1e3c72'
        },
        {
            icon: <ShowChartIcon sx={{ fontSize: 40 }} />,
            title: 'Grafikler ve Görseller',
            description: 'İnteraktif grafikler, ısı haritaları ve özet kartları. Zaman karşılaştırması (bugün / geçen hafta / geçen ay) ile trendleri kolayca takip edin.',
            color: '#1e3c72'
        },
        {
            icon: <FilterListIcon sx={{ fontSize: 40 }} />,
            title: 'Gelişmiş Filtreleme',
            description: 'Şehir, marka, kategori, fiyat aralığı gibi kriterlere göre kolay filtreleme. Sade, kullanıcı dostu arayüz ile hızlı sonuç alın.',
            color: '#1e3c72'
        },
        {
            icon: <GetAppIcon sx={{ fontSize: 40 }} />,
            title: 'Rapor İndirme',
            description: 'PDF veya CSV formatında rapor indirme özelliği. Analizlerinizi ekibinizle paylaşın, sunum yapın veya arşivleyin.',
            color: '#1e3c72'
        },
        {
            icon: <TimelineIcon sx={{ fontSize: 40 }} />,
            title: 'Zaman Serisi Analizi',
            description: 'Günlük, haftalık veya aylık trend raporları. Uzun dönemli raporlarla pazar dinamiklerini derinlemesine anlayın.',
            color: '#1e3c72'
        }
    ];

    // Faydalar
    const benefits = [
        {
            icon: <SpeedIcon />,
            title: 'Rekabet Avantajı',
            description: 'Fırsatları rakiplerden önce fark edin'
        },
        {
            icon: <ScheduleIcon />,
            title: 'Zaman Tasarrufu',
            description: 'Manuel veri incelemeye gerek kalmaz'
        },
        {
            icon: <CheckCircleIcon />,
            title: 'Doğru Kararlar',
            description: 'Veriye dayalı stratejik kararlar alın'
        },
        {
            icon: <AttachMoneyIcon />,
            title: 'Kârlılık Artışı',
            description: 'Talebe uygun ürünlere odaklanın'
        }
    ];

    // Kullanım alanları
    const useCases = [
        {
            icon: <StoreIcon />,
            title: 'E-Ticaret Satıcıları',
            description: 'Popüler ürünleri tespit edin, stok planınızı optimize edin. Hangi ürünler trend, hangileri düşüşte anında görün.',
            color: '#1e3c72'
        },
        {
            icon: <CampaignIcon />,
            title: 'Pazarlama Ekipleri',
            description: 'Kampanyalarınızı trend kategorilere yönlendirin. Hangi ürün gruplarına yatırım yapacağınızı veriye dayalı belirleyin.',
            color: '#1e3c72'
        },
        {
            icon: <BusinessCenterIcon />,
            title: 'Markalar & Tedarikçiler',
            description: 'Fiyat değişimlerini izleyin, strateji geliştirin. Pazar dinamiklerini anlayın, ürün portföyünüzü optimize edin.',
            color: '#1e3c72'
        },
        {
            icon: <AnalyticsIcon />,
            title: 'Pazar Analistleri',
            description: 'Uzun dönemli raporlarla içgörü elde edin. Detaylı analizler yapın, stratejik öneriler sunun.',
            color: '#1e3c72'
        }
    ];

    // Neler bekleyebilirsiniz
    const expectations = [
        'Günlük, haftalık veya aylık otomatik trend raporları',
        'Gerçek zamanlı veri güncelleme'
    ];

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#fafafa' }}>
            {/* Hero Section */}
            <Box
                sx={{
                    background: 'linear-gradient(135deg, #0b214bff 0%, #1e3c72 100%)',
                    color: 'white',
                    py: { xs: 8, md: 12 },
                    position: 'relative',
                    overflow: 'hidden'
                }}
            >
                {/* Animated Background */}
                <Box
                    component={motion.div}
                    animate={{
                        x: [0, 100, 0],
                        y: [0, 50, 0],
                        opacity: [0.3, 0.6, 0.3]
                    }}
                    transition={{
                        duration: 15,
                        repeat: Infinity,
                        ease: "easeInOut"
                    }}
                    sx={{
                        position: 'absolute',
                        top: -50,
                        left: -50,
                        width: 300,
                        height: 300,
                        borderRadius: '50%',
                        background: 'radial-gradient(circle, rgba(255,255,255,0.3) 0%, transparent 70%)'
                    }}
                />

                <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
                    <Grid container spacing={4} alignItems="center">
                        <Grid item xs={12} md={7}>
                            <motion.div
                                initial={{ opacity: 0, y: 30 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.8 }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                    <TrendingUpIcon sx={{ fontSize: 50, mr: 2 }} />
                                    <Typography variant="h1" component="h1" sx={{ fontWeight: 800, fontSize: { xs: '2.5rem', md: '3.5rem' } }}>
                                        Trend-CAT
                                    </Typography>
                                </Box>
                                <Typography variant="h5" sx={{ mb: 3, fontWeight: 300 }}>
                                    Akıllı Trend Analizi Platformu
                                </Typography>
                                <Typography variant="h6" sx={{ mb: 4, opacity: 0.95, lineHeight: 1.6 }}>
                                    İnternet verilerini analiz ederek size hangi ürünlerin, markaların veya kategorilerin yükselişte 
                                    olduğunu gösteren trend analiz platformu. Teknik detaylarla uğraşmadan, pazardaki 
                                    değişimleri anında görür, doğru hamleyi zamanında yaparsınız.
                                </Typography>
                            </motion.div>
                        </Grid>
                        <Grid item xs={12} md={5}>
                            <motion.div
                                initial={{ opacity: 0, scale: 0.8 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ duration: 0.8, delay: 0.2 }}
                            >
                                <Paper
                                    elevation={10}
                                    sx={{
                                        p: 3,
                                        borderRadius: 4,
                                        background: 'rgba(255, 255, 255, 0.95)',
                                        backdropFilter: 'blur(10px)'
                                    }}
                                >
                                    <Typography variant="h6" gutterBottom sx={{ color: '#1e3c72', fontWeight: 600, mb: 2 }}>
                                        📈 Haftalık Trend Raporu
                                    </Typography>
                                    <ResponsiveContainer width="100%" height={200}>
                                        <AreaChart data={trendData}>
                                            <defs>
                                                <linearGradient id="colorA" x1="0" y1="0" x2="0" y2="1">
                                                    <stop offset="5%" stopColor="#1e3c72" stopOpacity={0.8}/>
                                                    <stop offset="95%" stopColor="#1e3c72" stopOpacity={0}/>
                                                </linearGradient>
                                                <linearGradient id="colorB" x1="0" y1="0" x2="0" y2="1">
                                                    <stop offset="5%" stopColor="#1e3c72" stopOpacity={0.8}/>
                                                    <stop offset="95%" stopColor="#1e3c72" stopOpacity={0}/>
                                                </linearGradient>
                                            </defs>
                                            <CartesianGrid strokeDasharray="3 3" />
                                            <XAxis dataKey="date" />
                                            <YAxis />
                                            <Tooltip />
                                            <Area type="monotone" dataKey="urun_a" stroke="#1e3c72" fillOpacity={1} fill="url(#colorA)" name="Ürün A" />
                                            <Area type="monotone" dataKey="urun_b" stroke="#1e3c72" fillOpacity={1} fill="url(#colorB)" name="Ürün B" />
                                        </AreaChart>
                                    </ResponsiveContainer>  
                                    <Stack spacing={1} sx={{ mt: 2 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                            <Typography variant="body2">Elektronik</Typography>
                                            <Chip label="+42%" color="primary" size="small" />
                                        </Box>
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                            <Typography variant="body2">Giyim</Typography>
                                            <Chip label="+28%" color="primary" size="small" />
                                        </Box>
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                            <Typography variant="body2">Ev & Yaşam</Typography>
                                            <Chip label="+18%" color="primary" size="small" />
                                        </Box>
                                    </Stack>
                                </Paper>
                            </motion.div>
                        </Grid>
                    </Grid>
                </Container>
            </Box>

            {/* TrendCAT Ne Yapar? */}
            <Box sx={{ py: 8, bgcolor: 'white' }}>
                <Container maxWidth="lg">
                    <Box sx={{ textAlign: 'center', mb: 6 }}>
                        <Chip
                            label="Ne Yapar?"
                            color="primary"
                            sx={{ mb: 2, fontSize: '1rem', py: 3 }}
                        />
                        <Typography variant="h3" component="h2" gutterBottom sx={{ fontWeight: 700, color: '#1e3c72' }}>
                            Trend-CAT Ne Yapar?
                        </Typography>
                    </Box>

                    <Grid container spacing={4}>
                        {mainFeatures.map((feature, index) => (
                            <Grid item xs={12} md={6} key={index}>
                                <motion.div
                                    initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
                                    whileInView={{ opacity: 1, x: 0 }}
                                    transition={{ duration: 0.6, delay: index * 0.1 }}
                                    viewport={{ once: true }}
                                    style={{ height: '100%' }}
                                >
                                    <Card
                                        sx={{
                                            p: 3,
                                            height: '100%',
                                            minHeight: '200px',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            '&:hover': {
                                                boxShadow: 8,
                                                transform: 'translateY(-5px)',
                                                transition: 'all 0.3s ease'
                                            }
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                                            <Avatar
                                                sx={{
                                                    bgcolor: feature.color,
                                                    width: 70,
                                                    height: 70,
                                                    mr: 3
                                                }}
                                            >
                                                {feature.icon}
                                            </Avatar>
                                            <Box sx={{ flex: 1 }}>
                                                <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: feature.color }}>
                                                    {feature.title}
                                                </Typography>
                                                <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                                                    {feature.description}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Card>
                                </motion.div>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </Box>

            {/* Size Sağlayacağı Faydalar */}
            <Box sx={{ py: 8, bgcolor: '#f5f5f5' }}>
                <Container maxWidth="lg">
                    <Box sx={{ textAlign: 'center', mb: 6 }}>
                        <Chip
                            label="Faydalar"
                            color="primary"
                            sx={{ mb: 2, fontSize: '1rem', py: 3 }}
                        />
                        <Typography variant="h3" component="h2" gutterBottom sx={{ fontWeight: 700, color: '#1e3c72' }}>
                            Size Sağlayacağı Faydalar
                        </Typography>
                    </Box>

                    <Grid container spacing={4}>
                        {benefits.map((benefit, index) => (
                            <Grid item xs={12} sm={6} md={3} key={index}>
                                <motion.div
                                    initial={{ opacity: 0, y: 30 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.5, delay: index * 0.1 }}
                                    viewport={{ once: true }}
                                    style={{ height: '100%' }}
                                >
                                    <Card
                                        sx={{
                                            p: 3,
                                            height: '100%',
                                            minHeight: '220px',
                                            textAlign: 'center',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            '&:hover': {
                                                boxShadow: 6,
                                                transform: 'scale(1.05)',
                                                transition: 'all 0.3s ease'
                                            }
                                        }}
                                    >
                                        <Avatar
                                            sx={{
                                                bgcolor: 'primary.main',
                                                width: 70,
                                                height: 70,
                                                mx: 'auto',
                                                mb: 2
                                            }}
                                        >
                                            {benefit.icon}
                                        </Avatar>
                                        <Typography 
                                            variant="h6" 
                                            gutterBottom 
                                            sx={{ 
                                                fontWeight: 600,
                                                minHeight: '56px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center'
                                            }}
                                        >
                                            {benefit.title}
                                        </Typography>
                                        <Typography 
                                            variant="body2" 
                                            color="text.secondary"
                                            sx={{ lineHeight: 1.6, flex: 1 }}
                                        >
                                            {benefit.description}
                                        </Typography>
                                    </Card>
                                </motion.div>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </Box>

            {/* Kullanım Alanları */}
            <Box sx={{ py: 8, bgcolor: 'white' }}>
                <Container maxWidth="lg">
                    <Box sx={{ textAlign: 'center', mb: 6 }}>
                        <Chip
                            icon={<BusinessCenterIcon />}
                            label="Kullanım Alanları"
                            color="primary"
                            sx={{ mb: 2, fontSize: '1rem', py: 3 }}
                        />
                        <Typography variant="h3" component="h2" gutterBottom sx={{ fontWeight: 700, color: '#1e3c72' }}>
                            Kimler Kullanabilir?
                        </Typography>
                    </Box>

                    <Grid container spacing={4}>
                        {useCases.map((useCase, index) => (
                            <Grid item xs={12} md={6} key={index}>
                                <motion.div
                                    initial={{ opacity: 0, scale: 0.9 }}
                                    whileInView={{ opacity: 1, scale: 1 }}
                                    transition={{ duration: 0.6, delay: index * 0.15 }}
                                    viewport={{ once: true }}
                                    style={{ height: '100%' }}
                                >
                                    <Card
                                        sx={{
                                            p: 4,
                                            height: '100%',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            minHeight: '220px',
                                            '&:hover': {
                                                boxShadow: 10,
                                                transform: 'translateY(-10px)',
                                                transition: 'all 0.3s ease'
                                            }
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                                            <Avatar
                                                sx={{
                                                    bgcolor: useCase.color,
                                                    width: 64,
                                                    height: 64,
                                                    mr: 3
                                                }}
                                            >
                                                {useCase.icon}
                                            </Avatar>
                                            <Box sx={{ flex: 1 }}>
                                                <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: useCase.color, minHeight: '60px', display: 'flex', alignItems: 'center' }}>
                                                    {useCase.title}
                                                </Typography>
                                                <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                                                    {useCase.description}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Card>
                                </motion.div>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </Box>

            {/* Neler Bekleyebilirsiniz */}
            <Box sx={{ py: 8, bgcolor: '#f5f5f5' }}>
                <Container maxWidth="lg">
                    <Box sx={{ textAlign: 'center', mb: 6 }}>
                        <Chip
                            icon={<CheckCircleIcon />}
                            label="Neler Sunuyoruz?"
                            color="primary"
                            sx={{ mb: 2, fontSize: '1rem', py: 3 }}
                        />
                        <Typography variant="h3" component="h2" gutterBottom sx={{ fontWeight: 700, color: '#1e3c72' }}>
                            Neler Bekleyebilirsiniz?
                        </Typography>
                    </Box>

                    <Grid container spacing={3} justifyContent="center">
                        {expectations.map((expectation, index) => (
                            <Grid item xs={12} md={6} key={index}>
                                <motion.div
                                    initial={{ opacity: 0, x: -20 }}
                                    whileInView={{ opacity: 1, x: 0 }}
                                    transition={{ duration: 0.5, delay: index * 0.1 }}
                                    viewport={{ once: true }}
                                >
                                    <Card sx={{ p: 3, minHeight: '100px', display: 'flex', alignItems: 'center' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <CheckCircleIcon color="primary" sx={{ fontSize: 40, mr: 2 }} />
                                            <Typography variant="h6" sx={{ fontWeight: 500 }}>
                                                {expectation}
                                            </Typography>
                                        </Box>
                                    </Card>
                                </motion.div>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </Box>

            {/* Ücretsiz Demo Talep Et CTA */}
            <Box
                component={motion.div}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7 }}
                sx={{
                    py: { xs: 8, md: 12 },
                    background: 'linear-gradient(180deg, #0b1426 0%, #0a1020 100%)',
                    textAlign: 'center'
                }}
            >
                <Container maxWidth="md">
                    <Typography
                        variant="h2"
                        sx={{
                            fontWeight: 800,
                            color: '#ffffff',
                            mb: 3,
                            fontSize: { xs: '2rem', sm: '2.75rem', md: '3.5rem' },
                            textShadow: '2px 0 0 rgba(255,60,80,0.55), -2px 0 0 rgba(60,170,255,0.55)',
                            letterSpacing: '-0.5px'
                        }}
                    >
                        Ücretsiz Demo Talep Et
                    </Typography>
                    <Typography
                        sx={{
                            color: 'rgba(255,255,255,0.72)',
                            maxWidth: 720,
                            mx: 'auto',
                            mb: 5,
                            fontSize: { xs: '1rem', md: '1.125rem' },
                            lineHeight: 1.7
                        }}
                    >
                        Yapay zeka çözümlerimizin işletmenize katacağı değeri birlikte keşfedelim. Hemen bizimle iletişime geçin ve uzman ekibimizden demo talep edin.
                    </Typography>
                    <Button
                        size="large"
                        endIcon={<ArrowForwardIcon />}
                        onClick={() => window.open('https://dcat.com.tr/classic/tr/iletisim', '_blank')}
                        sx={{
                            px: 5,
                            py: 1.75,
                            fontSize: '1.05rem',
                            fontWeight: 700,
                            color: '#fff',
                            borderRadius: 999,
                            textTransform: 'none',
                            background: 'linear-gradient(90deg, #3b5cff 0%, #6a4cff 100%)',
                            boxShadow: '0 0 40px rgba(79, 92, 255, 0.55), 0 8px 24px rgba(0,0,0,0.35)',
                            transition: 'transform 0.25s ease, box-shadow 0.25s ease',
                            '&:hover': {
                                background: 'linear-gradient(90deg, #4a6bff 0%, #7a5cff 100%)',
                                boxShadow: '0 0 55px rgba(106, 76, 255, 0.7), 0 10px 28px rgba(0,0,0,0.4)',
                                transform: 'translateY(-2px)'
                            }
                        }}
                    >
                        Hemen Başlayın
                    </Button>
                </Container>
            </Box>
        </Box>
    );
};

export default TrendCAT;

